<template>
    <GLText
        ref="refTextBase"
        :text="text"
        :layer="LAYER_BG"
        :options="textOptions"
        :visible="false"
    ></GLText>
    <GLText
        ref="refText"
        name="TextLine"
        :text="finalText"
        :layer="layer"
        :options="textOptions"
        @sync="onTextSync"
        v-if="finalText"
    ></GLText>
</template>

<script setup>
    import { Mesh } from 'three';
    import { computed, onBeforeUnmount, reactive, ref, watchEffect } from 'vue';

    import { simpleMaterial, simplePlaneGeometry } from '@resn/gozer-three';
    import { disposeObject } from '@resn/gozer-three/helpers';
    import { useThreeObject } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import GLText from '~/components/gl/GLText.vue';
    import { LAYER_BG } from '~/core/constants';
    import { formatCategoryText } from '~/utils/text-utils';

    const props = defineProps({
        debug: { default: false },
        layer: { default: LAYER_BG },
        text: { default: 'Team Info' },
        bounds: { default: { width: 1, height: 1 } },
        position: { default: { x: 0, y: 0 } },
        dir: { default: 1 },
        depthWrite: { default: true },
        depthTest: { default: true },
        renderOrder: { default: 1 },
        options: { default: () => ({}) },
    });

    const refText = ref(null);
    const refTextBase = ref(null);

    const finalText = computed(() => {
        const baseTextWidth = refTextBase.value?.baseSize.width;
        const { width } = props.bounds;

        if (!props.text || !baseTextWidth || !width) return null;
        let iterations = Math.ceil(width / baseTextWidth);
        if (iterations % 2 === 0) iterations += 1;

        return props.text.repeat(iterations);
    });

    const { object } = useThreeObject(null, { name: 'TextLine' });
    const { object: innerObject } = useThreeObject(null, {
        name: 'TextLineInner',
        addToParent: false,
    });

    const bg = new Mesh(simplePlaneGeometry, simpleMaterial('red'));
    bg.layers.set(props.layer);
    bg.material.transparent = true;
    bg.material.opacity = 0.3;

    object.add(innerObject);
    innerObject.add(bg);

    const fontSize = computed(() => props.bounds.height * 1.27);
    const strokeWidth = computed(() => fontSize.value * 0.005);

    const textOptions = reactive({
        fontSize,
        anchorX: 'center',
        anchorY: 'middle',
        color: 'white',
        fillOpacity: 0,
        strokeColor: 'white',
        strokeWidth,
        strokeOpacity: 0.3,
        ...props.options,
    });

    const onTextSync = () => {
        updateLetters();
        const { mesh } = refText.value;

        mesh.material.depthWrite = props.depthWrite;
        mesh.material.depthTest = props.depthTest;
        mesh.renderOrder = props.renderOrder;
    };

    const updateLetters = () => {
        refText.value.updateLetters();
    };

    const show = ({ delay = 0 } = {}) => {};

    const setFontSize = (val) => {
        switch (textOptions.anchorY) {
            case 'middle':
                innerObject.position.y = fontSize.value * -0.05;
                break;
            case 'top':
                innerObject.position.y = fontSize.value * 0.05;
                break;
            case 'bottom':
                innerObject.position.y = fontSize.value * -0.14;
                break;
        }
    };

    const setPosition = () => {
        const { x, y } = props.position;
        object.position.set(x, y, 0);
    };

    const setBounds = () => {
        const { width, height } = props.bounds;
        const bgHeight = height * 0.95;

        let x = 0;
        switch (textOptions.anchorX) {
            case 'left':
                x = width / 2;
                break;
            case 'right':
                x = -width / 2;
                break;
        }

        let y = 0;
        switch (textOptions.anchorY) {
            case 'middle':
                y = bgHeight * 0.07;
                break;
            case 'top':
                y = bgHeight * -0.6;
                break;
            case 'bottom':
                y = bgHeight * 0.72;
                break;
        }
        bg.scale.set(width, bgHeight, 1);
        bg.position.set(x, y, 0);
    };

    const setDebug = () => {
        bg.visible = props.debug;
    };

    onBeforeUnmount(() => {
        disposeObject(object);
    });

    watchEffect(setPosition);
    watchEffect(setBounds);
    watchEffect(setDebug);
    watchEffect(setFontSize);

    defineExpose({ show, object, innerObject, text: refText, updateLetters });
</script>
